// 📁 src/components/IdeaPopup.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axiosInstance";

const IdeaPopup = ({
  idea,
  type,
  onClose,
  aiMessageOverride,
  autoClose = false,
  autoCloseDelay = 3500,
  onSaveImprovement,
}) => {
  const navigate = useNavigate();
  const [aiMessage, setAiMessage] = useState(aiMessageOverride || "");
  const [loading, setLoading] = useState(!aiMessageOverride);
  const [error, setError] = useState("");
  const [editedIdea, setEditedIdea] = useState(idea?.description || "");

  // Auto-close for lightweight popups
  useEffect(() => {
    if (autoClose) {
      const t = setTimeout(onClose, autoCloseDelay);
      return () => clearTimeout(t);
    }
  }, [autoClose, autoCloseDelay, onClose]);

  // Fetch AI popup (only when no override is provided)
  useEffect(() => {
    if (aiMessageOverride) return;

    const fetchAI = async () => {
      try {
        setLoading(true);
        const res = await axios.post("/api/ai-popups", { idea, type });
        setAiMessage(res.data.message);
      } catch {
        setError("⚠️ Failed to fetch AI insights.");
      } finally {
        setLoading(false);
      }
    };

    fetchAI();
  }, [idea, type, aiMessageOverride]);

  // Navigation to Market Research
  const handleMarketPotential = () => {
    navigate("/market-research", {
      state: { idea: `${idea.title} - ${idea.description}` },
    });
    onClose();
  };

  const handleSaveImprovement = () => {
    if (onSaveImprovement) {
      onSaveImprovement({ ...idea, description: editedIdea });
    }
    onClose();
  };

  /* ------------------------------------------------------------------
     🎯 Render modes
  ------------------------------------------------------------------ */
  const isLite = type === "tip"; // motivation popup for other modules

  if (isLite) {
    return (
      <div className="fixed bottom-6 right-6 bg-blue-600 text-white px-4 py-3 rounded-lg shadow-lg z-50 text-sm max-w-xs animate-fade-in">
        {aiMessage || "You're making great progress — keep going!"}
      </div>
    );
  }

  // Full popup version (Idea Generation)
  const titleMap = {
    feedback: "💬 Mentor Feedback",
    improve: "🛠️ Improve Your Idea",
    potential: "📊 Market Potential",
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-4 border-b">
          <h2 className="text-xl font-bold mb-1">{titleMap[type]}</h2>
          <h3 className="text-lg font-semibold text-blue-700">
            {idea.title}
          </h3>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {loading && <p className="text-gray-500">⏳ Analyzing...</p>}
          {error && <p className="text-red-500">{error}</p>}

          {!loading && !error && (
            <div className="bg-gray-100 p-3 rounded text-sm whitespace-pre-line mb-4">
              {aiMessage}
            </div>
          )}

          {/* Improve mode input */}
          {type === "improve" && !loading && (
            <>
              <textarea
                value={editedIdea}
                onChange={(e) => setEditedIdea(e.target.value)}
                rows={4}
                className="w-full border p-2 rounded text-sm mb-3"
              />
              <button
                onClick={handleSaveImprovement}
                className="bg-green-600 text-white px-4 py-2 rounded text-sm"
              >
                💾 Save Improved Idea
              </button>
            </>
          )}

          {/* Navigate to Market Research */}
          {type === "potential" && !loading && (
            <button
              onClick={handleMarketPotential}
              className="bg-blue-600 text-white px-4 py-2 rounded text-sm"
            >
              🚀 Go to Market Research Suite
            </button>
          )}
        </div>

        <div className="p-3 border-t text-right">
          <button
            onClick={onClose}
            className="bg-gray-700 text-white px-4 py-1 rounded text-sm"
          >
            ✖ Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default IdeaPopup;
